import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "oidc-react";
import { PdsLoadingPage } from "@principal/design-system-react";
import { Form, Formik } from "formik";
import Button from "../button/Button";
import BackButton from "../button/BackButton";
import { ButtonClass } from "../enums/ButtonClass";
import { BenefitType } from "../enums/BenefitType";
import { Party } from "../enums/Party";
import EmailInput from "../input/EmailInput";
import { string } from "yup";

const VerifyEmail = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useAuth();
    const benefitType = params?.coverage as BenefitType;
    const party = params?.party as Party;
    const emailSentPath = `/${benefitType}/${party}/verify-email/sent`;
    const returnLocation = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    const returnPath = returnLocation?.pathname
        ? `${returnLocation.pathname}${returnLocation.search ?? ''}`
        : `/${benefitType}/${party}`;
    const claimId = returnLocation?.pathname?.match(
        /^\/[^/]+\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/[^/]+\/?$/i
    )?.[1];
    const [emailAddress, setEmailAddress] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (auth?.userData) {
        return <Navigate to={returnPath} replace />;
    }

    if (isSubmitting) {
        return <PdsLoadingPage />;
    }

    return (
        <Formik initialValues={{ guestEmailAddress: emailAddress }}
            onSubmit={(values) => navigate(emailSentPath, { state: { emailAddress: values.guestEmailAddress } })}>
            {() =>
                <div className="pds-u-padding-top-40 pds-u-padding-bottom-40">
                    <Form>
                        <div className="pds-u-padding-bottom-24">
                            <h2>Verify your email</h2>
                            <div>We will send you an email with a unique link to complete the form.</div>
                        </div>
                        <div className="pds-u-padding-bottom-32" style={{ width: "260px" }}>
                            <EmailInput id="guest-email-address" label="Your email address" required={true}
                                changeCallback={(input: HTMLInputElement) => setEmailAddress(input.value)} />
                        </div>
                        <div className="pds-u-padding-bottom-80">
                            <Button mainButtonClass={ButtonClass.PRIMARY} onClick={handleSubmit}
                                buttonContent={<div style={{ width: "90px" }}>Submit</div>} />
                        </div>
                    </Form>
                    <BackButton onClick={handleBack} />
                </div>}
        </Formik>
    );

    async function handleSubmit() {
        const currentEmailAddress = findInputValue('guest-email-address') || emailAddress;
        if (!isValidEmail(currentEmailAddress)) {
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_FORMS_SERVICE_URL}/generate-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: currentEmailAddress,
                    benefitType,
                    party,
                    ...(claimId ? { claimId } : {})
                }),
            });
            if (!response.ok) {
                throw new Error(`generate-link failed with status ${response.status}`);
            }
            navigate(emailSentPath, { state: { emailAddress: currentEmailAddress } });
        } catch {
            setIsSubmitting(false);
        }
    }

    function findInputValue(id: string) {
        return (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
    }

    function handleBack() {
        const loginPath = `/${benefitType}/${party}/login`;
        if (returnLocation) {
            navigate(loginPath, { state: { from: returnLocation } });
            return;
        }
        navigate(loginPath);
    }

    function isValidEmail(value: string) {
        return !!value && string().email().isValidSync(value);
    }

};
export default VerifyEmail;